import React from 'react';
import { Avatar } from '@chakra-ui/react';
import { getOptimizedAvatar } from '../../utils/imageOptimizer';

// Real tier-1 avatar that exists on the CDN (default-avatar.png is a 404)
const DEFAULT_AVATAR_URL = "https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png";

// Validate that avatar URL is a real URL (not "default" or "https://undefined/...")
const isValidAvatarUrl = (url) => url && typeof url === 'string' && url.startsWith('https://') && !url.includes('://undefined');

/**
 * BurrowAvatar — thin wrapper around Chakra's Avatar
 * 
 * What it does:
 *   1. Validates the URL (rejects garbage like "default" or "https://undefined/...")
 *   2. Appends Bunny CDN optimization class (?class=avatarlarge/med/small)
 *   3. Passes everything to Chakra's native <Avatar> which handles loading/fallback
 * 
 * What it does NOT do (by design):
 *   - No custom new Image() preloading (Chakra's useImage already does this)
 *   - No loading="lazy" (causes display issues in drawers/modals)
 *   - No spinner/loading state (avatar is pre-cached at login time via preloadAvatarImage)
 *   - No custom error state (Chakra falls back to name initials automatically)
 * 
 * The "video game loading screen" approach: preload happens at login time in
 * Redux actions (fetchUserProfile / updateUserAvatar), so by the time any
 * BurrowAvatar renders, the image is already in the browser's HTTP cache.
 */
const BurrowAvatar = React.forwardRef((props, ref) => {
  const { src, name, size = 'md', ...rest } = props;
  
  // Validate and normalize the src
  const validatedSrc = isValidAvatarUrl(src) ? src : DEFAULT_AVATAR_URL;
  
  // Optimize avatar image based on size — appends ?class=avatarlarge/med/small
  const avatarSize = size === 'xl' || size === '2xl' ? 'large' : size === 'xs' || size === '2xs' ? 'small' : 'medium';
  const optimizedSrc = getOptimizedAvatar(validatedSrc, avatarSize);
  
  return (
    <Avatar
      ref={ref}
      src={optimizedSrc}
      name={name}
      size={size}
      {...rest}
    />
  );
});

BurrowAvatar.displayName = 'BurrowAvatar';

export { DEFAULT_AVATAR_URL, isValidAvatarUrl };
export default BurrowAvatar;
